import React, { useState, useRef, useEffect } from 'react';
import { 
  Bot, 
  X, 
  Send, 
  Calendar, 
  RotateCw
} from 'lucide-react';
import { ChatMessage } from '../types';

interface LiveAIAssistantWidgetProps {
  onOpenBooking: () => void;
}

export const LiveAIAssistantWidget: React.FC<LiveAIAssistantWidgetProps> = ({
  onOpenBooking,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      content:
        'Hello! I am Xweba’s AI Growth Concierge. We build websites engineered for both humans and AI search engines like ChatGPT and Perplexity. What questions can I answer about our AI packages, GEO, or business automation?',
      timestamp: 'Just now',
    },
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const quickPrompts = [
    'What is GEO vs traditional SEO?',
    'Which package is best for my business?',
    'Domain & Hostinger partner hosting?',
    'How does Website-as-a-Service work?',
    'Can we pay in KSh (KES)?',
  ];

  const handleSendMessage = async (textToSend?: string) => {
    const text = textToSend || input.trim();
    if (!text || isLoading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: 'Just now',
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [...messages, userMsg].map((m) => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      const data = await response.json();
      if (data.success && data.reply) {
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: data.reply,
            timestamp: 'Just now',
          },
        ]);
      } else {
        throw new Error('API fallback');
      }
    } catch (err) {
      // Graceful fallback response
      let fallbackText = "At Xweba, we build AI-ready websites designed for both humans and AI engines like ChatGPT and Perplexity. Would you like to explore our AI Growth package or run a quick scan on your existing site?";
      const lower = text.toLowerCase();
      if (lower.includes('geo') || lower.includes('seo') || lower.includes('search')) {
        fallbackText = "GEO (Generative Engine Optimization) structures your website so ChatGPT, Perplexity, and Google AI Overviews cite and recommend your business directly. Unlike old keyword SEO, GEO builds machine-readable entity graphs and citation hooks!";
      } else if (lower.includes('price') || lower.includes('package') || lower.includes('cost') || lower.includes('ksh') || lower.includes('usd')) {
        fallbackText = "We offer 4 productized packages: AI Starter, AI Growth, AI Pro, and AI Enterprise. Available as a one-off build or our popular Website-as-a-Service (Setup + Monthly Growth) model in both USD and KSh!";
      } else if (lower.includes('book') || lower.includes('contact') || lower.includes('call')) {
        fallbackText = "You can schedule a free 20-minute AI Growth Strategy Session right now using our booking calendar or connect with us directly on WhatsApp!";
      }

      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: fallbackText,
          timestamp: 'Just now',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-3 right-3 sm:bottom-5 sm:right-5 z-40">
      {/* Floating Trigger Button */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="group flex items-center justify-center gap-2.5 bg-[#0B2D52] hover:bg-[#071E36] text-white p-3.5 min-h-[48px] min-w-[48px] sm:px-5 sm:py-3.5 rounded-full shadow-xl shadow-slate-900/20 border-2 border-white/20 transition-all transform hover:scale-105 active:scale-95 cursor-pointer"
          aria-label="Open Xweba AI Assistant"
        >
          <div className="relative">
            <Bot className="w-5 h-5 text-[#F26522]" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#F26522] rounded-full animate-ping" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#F26522] rounded-full" />
          </div>
          <div className="hidden sm:flex flex-col text-left">
            <span className="text-xs font-bold leading-tight flex items-center gap-1">
              Ask Xweba AI <span className="text-[#F26522]">Concierge</span>
            </span>
            <span className="text-[10px] text-slate-300">24/7 Instant Growth Advisory</span>
          </div>
        </button>
      )}

      {/* Expanded Chat Window */}
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[calc(100vw-1.5rem)] sm:w-[380px] h-[min(520px,calc(100dvh-5rem))] flex flex-col overflow-hidden animate-fadeIn">
          
          {/* Header */}
          <div className="bg-[#0B2D52] p-3.5 sm:p-4 text-white flex items-center justify-between border-b border-slate-800 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 border border-orange-500/40 flex items-center justify-center text-[#F26522] shrink-0">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold flex items-center gap-1.5">
                  Xweba AI Concierge
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                </h4>
                <p className="text-[10px] text-slate-300">Live AI Search &amp; Conversion Advisor</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-2 min-w-[36px] min-h-[36px] flex items-center justify-center text-slate-400 hover:text-white rounded-lg hover:bg-slate-800/80 transition-colors cursor-pointer"
              aria-label="Close Chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Conversation Body */}
          <div className="flex-1 p-3.5 sm:p-4 overflow-y-auto space-y-3 bg-slate-50 text-xs">
            {messages.map((m) => {
              const isAssistant = m.role === 'assistant';
              return (
                <div
                  key={m.id}
                  className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl p-3 leading-relaxed ${
                      isAssistant
                        ? 'bg-white text-slate-800 border border-slate-200 shadow-2xs rounded-tl-xs'
                        : 'bg-[#F26522] text-white rounded-tr-xs font-medium'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              );
            })}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 text-slate-500 p-3 rounded-2xl rounded-tl-xs flex items-center gap-2 text-xs">
                  <RotateCw className="w-3.5 h-3.5 animate-spin text-[#F26522]" />
                  <span>Synthesizing response...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts Carousel */}
          <div className="p-2 bg-white border-t border-slate-100 flex gap-1.5 overflow-x-auto text-[11px] no-scrollbar shrink-0">
            {quickPrompts.map((qp, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSendMessage(qp)}
                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-full whitespace-nowrap transition-colors border border-slate-200 shrink-0 cursor-pointer"
              >
                {qp}
              </button>
            ))}
          </div>

          {/* Action Bar (Schedule Call or Chat Input) */}
          <div className="p-3 bg-white border-t border-slate-200 space-y-2 shrink-0">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about GEO, pricing, or strategy..."
                className="flex-1 min-w-0 px-3 py-2 min-h-[42px] bg-slate-50 border border-slate-200 rounded-xl text-base sm:text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-[#F26522] focus:bg-white transition-all"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="p-2.5 min-w-[42px] min-h-[42px] flex items-center justify-center bg-[#F26522] hover:bg-[#ea580c] disabled:opacity-50 text-white rounded-xl transition-all cursor-pointer shrink-0"
                aria-label="Send Message"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>

            <div className="flex items-center justify-between text-[11px] pt-1">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onOpenBooking();
                }}
                className="text-[#0B2D52] font-bold hover:text-[#F26522] flex items-center gap-1 cursor-pointer py-0.5"
              >
                <Calendar className="w-3 h-3 text-[#F26522]" />
                Book Strategy Session
              </button>
              <span className="text-slate-400">Powered by Xweba AI</span>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};
